import styled, { css, keyframes } from "styled-components";
import colors from "@streeteasy/design-system/deprecated/colors";

import { POSITION, THEME } from "./constants";

const setShape = position => {
  switch (position) {
    case POSITION.TOP:
      return css`
        bottom: auto;
        left: 50%;
        right: 50%;
        top: calc(100% - 4px);
        transform: translateX(-50%) rotate(270deg);
      `;
    case POSITION.RIGHT:
      return css`
        bottom: auto;
        left: auto;
        right: calc(100% - 1px);
        top: 50%;
        transform: translateY(-50%);
      `;
    case POSITION.BOTTOM:
      return css`
        bottom: calc(100% - 4px);
        left: 50%;
        right: 50%;
        top: auto;
        transform: translateX(-50%) rotate(90deg);
      `;
    case POSITION.LEFT:
      return css`
        bottom: auto;
        left: calc(100% - 1px);
        right: auto;
        top: 50%;
        transform: translateY(-50%) rotate(180deg);
      `;
    default:
      return css`
        bottom: auto;
        left: auto;
        right: calc(100% - 2px);
        top: 50%;
        transform: translateY(-50%);
      `;
  }
};

const setTooltipPosition = position => {
  switch (position) {
    case POSITION.TOP:
      return css`
        bottom: calc(100% + 12px);
        left: 50%;
        right: auto;
        top: auto;
        transform: translateX(-50%);
      `;
    case POSITION.RIGHT:
      return css`
        bottom: auto;
        left: calc(100% + 6px);
        right: auto;
        top: 50%;
        transform: translateY(-50%);
      `;
    case POSITION.BOTTOM:
      return css`
        bottom: auto;
        left: 50%;
        right: auto;
        top: calc(100% + 12px);
        transform: translateX(-50%);
      `;
    case POSITION.LEFT:
      return css`
        bottom: auto;
        left: auto;
        right: calc(100% + 6px);
        top: 50%;
        transform: translateY(-50%);
      `;
    default:
      return css`
        bottom: calc(100% + 12px);
        left: 50%;
        right: auto;
        top: auto;
      `;
  }
};

const setTooltipStyles = props => {
  if (props.theme === THEME.SMALL) {
    return css`
      background-color: ${colors.pureWhite};
      font-size: 12px;
      line-height: 16px;
      padding: 16px 24px 20px;
      text-align: center;
      width: ${props.width || "192px"};
      z-index: ${props.zIndex};
    `;
  }

  return css`
    background-color: ${colors.mouseGrey};
    font-size: 14px;
    line-height: 22px;
    padding: 20px;
    text-align: left;
    width: ${props.width || "280px"};
    z-index: ${props.zIndex};
  `;
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    visibility: hidden;
  }
  to {
    opacity: 1;
    visibility: visible;
  }
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  position: absolute;
  right: 15px;
  top: 12px;
`;
CloseButton.displayName = "CloseButton";

export const CloseButtonScreenReaderText = styled.span`
  border: 0;
  clip: rect(1px, 1px, 1px, 1px); /* for Internet Explorer */
  clip: rect(1px 1px 1px 1px);
  height: 1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

export const TooltipWrapper = styled.span`
  display: inline-block;
  position: relative;
  vertical-align: middle;

  * {
    -webkit-font-smoothing: antialiased;
    -webkit-text-size-adjust: 100%;
  }

  *,
  *::after,
  *::before {
    box-sizing: unset;
  }
`;
TooltipWrapper.displayName = "TooltipWrapper";

export const TooltipContentWrapper = styled.div`
  animation: ${fadeIn} 300ms ease-in-out 1;
  border: 1px solid ${colors.rabbitGrey};
  border-radius: 3px;
  box-shadow: 1px 2px 5px 0 rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  color: ${colors.whaleGrey};
  font-family: "Source Sans Pro", Helvetica, Geneva, Arial, sans-serif;
  font-weight: 400;
  position: absolute;
  transition: visibility 300ms ease-in-out;

  ${props => setTooltipPosition(props.position)};
  ${props => setTooltipStyles(props)};
`;
TooltipContentWrapper.displayName = "TooltipContentWrapper";

export const Trigger = styled.i`
  color: ${colors.goatGrey};
  display: inline-block;
  font-size: 14px;

  &:hover {
    cursor: pointer;
  }
`;
Trigger.displayName = "TooltipTrigger";

export const TriggerContainer = styled.button.attrs(props => ({
  type: "button",
  "aria-label": props["aria-label"],
  "aria-disabled": "true",
  title: props.buttonTitle
}))`
  appearance: none;
  background: none;
  border: none;
  padding: 0;

  &:hover {
    cursor: pointer;
  }
`;
TriggerContainer.displayName = "TriggerContainer";

export const TooltipTriangle = styled.span`
  display: inline-block;
  position: absolute;

  ${props => setShape(props.position)};

  &:before {
    color: ${props =>
      props.theme === THEME.SMALL ? colors.pureWhite : colors.mouseGrey};
    content: "\f0d9";
    display: block;
    font-family: FontAwesome;
    font-size: 23px;
    line-height: 22px;
    text-shadow: -1px 0 ${colors.rabbitGrey};
    z-index: 10;
  }
`;
TooltipTriangle.displayName = "TooltipTriangle";
